/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

export interface JwtPayload {
  [key: string]: unknown;
  exp?: number;
  iat?: number;
  sub?: string;
  admin_id?: number; // Admin user indicator
  uid?: number; // User ID
  utypid?: number; // User type ID
}

/**
 * Decodes JWT token payload without verification
 * JWT format: header.payload.signature
 * Only decodes the payload part (base64url encoded)
 *
 * @param token - JWT token string
 * @returns Decoded payload object or null if invalid
 */
export const decodeJwtToken = (token: string): JwtPayload | null => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    // Split JWT into parts
    const parts = token.split('.');

    if (parts.length !== 3) {
      console.error('[decodeJwtToken] Invalid JWT format: expected 3 parts');
      return null;
    }

    // Get payload (second part)
    const payload = parts[1];

    // Base64url decode: replace URL-safe chars and add padding if needed
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );

    // Decode base64 to JSON string
    const jsonPayload = atob(paddedBase64);

    // Parse JSON
    return JSON.parse(jsonPayload) as JwtPayload;
  } catch (error) {
    console.error('[decodeJwtToken] Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Checks if the JWT token belongs to an admin user
 *
 * @param token - JWT token string
 * @returns true if token indicates admin user, false otherwise
 */
export const isAdminToken = (token: string): boolean => {
  const payload = decodeJwtToken(token);

  if (!payload) {
    return false;
  }

  // Check for admin_id as a positive number
  if (typeof payload.admin_id === 'number' && payload.admin_id > 0) {
    return true;
  }

  return false;
};

/**
 * Gets JWT token expiration as Max-Age string for cookie
 *
 * @param token - JWT token string
 * @param defaultMaxAge - Default Max-Age string to return if token is invalid or missing exp claim (e.g., "Max-Age=3600")
 * @returns Max-Age string for cookie (e.g., "Max-Age=3600") based on token exp or defaultMaxAge
 */
export const getTokenExpiration = (
  token: string,
  defaultMaxAge: string
): string => {
  const payload = decodeJwtToken(token);

  // Check if payload exists and exp is a valid positive number
  if (payload && typeof payload.exp === 'number' && payload.exp > 0) {
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const secondsRemaining = Math.max(0, payload.exp - now);
    return `Max-Age=${secondsRemaining}`;
  }

  return defaultMaxAge;
};